import { useEffect, useRef, useState } from "react";
import { FiCamera, FiLoader, FiUser } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/apiFetch";
import { saveDisplayUser } from "../utils/authStorage";
import { useQueryClient } from "@tanstack/react-query";

export default function ProfilePicture({ compact = false }) {
  const { userId, userInfo, setUserInfo } = useAuth();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const request = useRef(null);
  useEffect(() => () => {
    request.current?.abort();
  }, [userId]);
  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;
    setError(""); setSaved(false);
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError("Choose a JPG, PNG, or WebP image up to 5 MB."); return;
    }
    setBusy(true);
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/profile/picture`, {
        method: "POST", headers: { "Content-Type": file.type }, body: file, signal: controller.signal,
      });
      const data = await response.json();
      if (controller.signal.aborted) return;
      if (!response.ok) throw new Error(data.message || "Could not upload profile picture.");
      setUserInfo(previous => {
        if (controller.signal.aborted || !previous) return previous;
        const updated = { ...previous, id: userId, avatar: data.avatar };
        saveDisplayUser(updated);
        return updated;
      });
      void queryClient.invalidateQueries();
      setSaved(true);
    } catch (error) { if (!controller.signal.aborted) setError(error.message); }
    finally { if (!controller.signal.aborted) setBusy(false); }
  };
  if (compact) return <div className="relative shrink-0"><label className="relative flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[var(--app-raised)]">
      {busy ? <FiLoader className="h-6 w-6 animate-spin" /> : userInfo?.avatar ? <img src={userInfo.avatar} alt="Your profile" className="h-full w-full object-cover" /> : <FiUser className="h-8 w-8" />}
      <input aria-label="Change profile picture" type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={upload} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
    </label>{error && <p role="alert" className="mt-2 max-w-xs text-xs text-red-600">{error}</p>}</div>;
  return <div className="mt-6 space-y-3">
    <div className="flex items-center gap-4">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100">
        {userInfo?.avatar ? <img src={userInfo.avatar} alt="Your profile" className="h-full w-full object-cover" /> : <FiUser className="h-8 w-8" />}
      </div>
      <label className={`relative inline-flex min-h-11 items-center gap-2 rounded-md border border-[var(--app-border)] px-4 py-2 font-semibold ${busy ? "opacity-60" : "cursor-pointer hover:bg-gray-100"}`}>
        {busy ? <FiLoader className="animate-spin" /> : <FiCamera />} {busy ? "Uploading..." : "Upload photo"}
        <input aria-label="Upload profile picture" type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={upload} className="absolute inset-0 w-full cursor-pointer opacity-0" />
      </label>
    </div>
    <p className="text-xs text-[var(--app-secondary)]">JPG, PNG or WebP. Maximum 5 MB.</p>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {saved && <p role="status" className="text-sm text-[var(--app-secondary)]">Profile picture updated.</p>}
  </div>;
}
